<template>
  <div class="container py-5">
    <div class="text-center mb-5">
      <h1 class="display-4 fw-bold">About Elderly Care Services</h1>
      <p class="lead">
        Dedicated to enhancing the quality of life for senior citizens through comprehensive care
        and support.
      </p>
    </div>

    <div class="row g-5 align-items-center mb-5">
      <div class="col-md-6">
        <div class="position-relative">
          <img
            src="../assets/images/Snipaste_2025-07-23_11-39-31.png"
            class="img-fluid rounded-4 shadow-sm"
            alt="About Us"
            style="width: 100%; max-width: 600px"
          />
          <div class="position-absolute top-0 start-0 p-3">
            <span class="badge bg-primary">Since 2020</span>
          </div>
        </div>
      </div>

      <div class="col-md-6">
        <h2 class="fw-bold mb-4">Our Commitment to Senior Care</h2>
        <p class="mb-4">
          At Elderly Care Services, we believe that every senior deserves to live with dignity,
          comfort, and security. Our organization was founded on the principle that aging should not
          mean losing independence or quality of life.
        </p>

        <div class="d-flex align-items-start gap-3 mb-3">
          <i class="bi bi-check-circle-fill fs-4 text-primary"></i>
          <div>
            <h5 class="fw-bold">Compassionate Approach</h5>
            <p class="mb-0">
              We treat every individual with respect and empathy, understanding their unique needs.
            </p>
          </div>
        </div>

        <div class="d-flex align-items-start gap-3 mb-3">
          <i class="bi bi-check-circle-fill fs-4 text-primary"></i>
          <div>
            <h5 class="fw-bold">Professional Excellence</h5>
            <p class="mb-0">
              Our caregivers are highly trained professionals committed to providing quality
              services.
            </p>
          </div>
        </div>

        <div class="d-flex align-items-start gap-3 mb-3">
          <i class="bi bi-check-circle-fill fs-4 text-primary"></i>
          <div>
            <h5 class="fw-bold">Continuous Innovation</h5>
            <p class="mb-0">
              We continually improve our services based on feedback and advancements in elder care.
            </p>
          </div>
        </div>

        <div class="mt-4">
          <a href="/contact" class="btn btn-primary btn-lg">Contact Us</a>
        </div>
      </div>
    </div>

    <div class="mt-5 pt-5">
      <h2 class="text-center fw-bold mb-5">Our Core Values</h2>
      <div class="row g-4">
        <div class="col-md-4">
          <div class="card h-100 border-0 shadow-sm hover-lift">
            <div class="card-body d-flex flex-column align-items-center text-center p-4">
              <div
                class="d-flex align-items-center justify-content-center w-50px h-50px rounded-circle bg-primary bg-opacity-10 mb-3"
              >
                <i class="bi bi-heart fs-3 text-primary"></i>
              </div>
              <h5 class="card-title fw-bold">Compassion</h5>
              <p class="card-text">
                We approach every interaction with empathy, understanding, and genuine care for
                others.
              </p>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card h-100 border-0 shadow-sm hover-lift">
            <div class="card-body d-flex flex-column align-items-center text-center p-4">
              <div
                class="d-flex align-items-center justify-content-center w-50px h-50px rounded-circle bg-primary bg-opacity-10 mb-3"
              >
                <i class="bi bi-shield-check fs-3 text-primary"></i>
              </div>
              <h5 class="card-title fw-bold">Integrity</h5>
              <p class="card-text">
                We maintain the highest standards of honesty and ethical behavior in all our
                interactions.
              </p>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card h-100 border-0 shadow-sm hover-lift">
            <div class="card-body d-flex flex-column align-items-center text-center p-4">
              <div
                class="d-flex align-items-center justify-content-center w-50px h-50px rounded-circle bg-primary bg-opacity-10 mb-3"
              >
                <i class="bi bi-people fs-3 text-primary"></i>
              </div>
              <h5 class="card-title fw-bold">Community</h5>
              <p class="card-text">
                We foster connections between seniors, families, and caregivers to build supportive
                networks.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-5 pt-5">
      <h2 class="text-center fw-bold mb-5">Our Services</h2>
      <div class="row g-4">
        <div class="col-md-6 col-lg-4">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body d-flex flex-column">
              <h3 class="h5 fw-bold mb-3">Personal Care</h3>
              <ul class="list-check">
                <li>
                  <i class="bi bi-check2-circle text-success me-2"></i>Assistance with daily living
                  activities
                </li>
                <li>
                  <i class="bi bi-check2-circle text-success me-2"></i>Bathing, dressing, grooming
                  support
                </li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>Medication reminders</li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>Meal preparation</li>
              </ul>
            </div>
          </div>
        </div>
        <div class="col-md-6 col-lg-4">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body d-flex flex-column">
              <h3 class="h5 fw-bold mb-3">Health Management</h3>
              <ul class="list-check">
                <li>
                  <i class="bi bi-check2-circle text-success me-2"></i>Health monitoring and
                  tracking
                </li>
                <li>
                  <i class="bi bi-check2-circle text-success me-2"></i>Doctor appointment
                  coordination
                </li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>Rehabilitation support</li>
                <li>
                  <i class="bi bi-check2-circle text-success me-2"></i>Chronic condition management
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div class="col-md-6 col-lg-4">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body d-flex flex-column">
              <h3 class="h5 fw-bold mb-3">Social Engagement</h3>
              <ul class="list-check">
                <li>
                  <i class="bi bi-check2-circle text-success me-2"></i>Companionship and
                  conversation
                </li>
                <li>
                  <i class="bi bi-check2-circle text-success me-2"></i>Recreational activities
                </li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>Community outings</li>
                <li><i class="bi bi-check2-circle text-success me-2"></i>Technology assistance</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-5 pt-5">
      <h2 class="text-center fw-bold mb-5">Meet Our Leadership Team</h2>
      <div class="row g-4">
        <div class="col-md-6 col-lg-3">
          <div class="card border-0 shadow-sm">
            <img
              src="https://placehold.co/300x300/4e73df/ffffff?text=John+Doe"
              class="card-img-top rounded-circle"
              alt="Team Member"
            />
            <div class="card-body text-center">
              <h5 class="card-title fw-bold">John Doe</h5>
              <p class="card-text text-muted mb-0">Founder & CEO</p>
              <p class="card-text mt-2">20+ years of experience in elder care services.</p>
            </div>
          </div>
        </div>
        <div class="col-md-6 col-lg-3">
          <div class="card border-0 shadow-sm">
            <img
              src="https://placehold.co/300x300/4e73df/ffffff?text=Jane+Smith"
              class="card-img-top rounded-circle"
              alt="Team Member"
            />
            <div class="card-body text-center">
              <h5 class="card-title fw-bold">Jane Smith</h5>
              <p class="card-text text-muted mb-0">Chief Operations Officer</p>
              <p class="card-text mt-2">Expert in care coordination and service delivery.</p>
            </div>
          </div>
        </div>
        <div class="col-md-6 col-lg-3">
          <div class="card border-0 shadow-sm">
            <img
              src="https://placehold.co/300x300/4e73df/ffffff?text=Michael+Lee"
              class="card-img-top rounded-circle"
              alt="Team Member"
            />
            <div class="card-body text-center">
              <h5 class="card-title fw-bold">Michael Lee</h5>
              <p class="card-text text-muted mb-0">Director of Client Services</p>
              <p class="card-text mt-2">Passionate about personalized care solutions.</p>
            </div>
          </div>
        </div>
        <div class="col-md-6 col-lg-3">
          <div class="card border-0 shadow-sm">
            <img
              src="https://placehold.co/300x300/4e73df/ffffff?text=Sarah+Johnson"
              class="card-img-top rounded-circle"
              alt="Team Member"
            />
            <div class="card-body text-center">
              <h5 class="card-title fw-bold">Sarah Johnson</h5>
              <p class="card-text text-muted mb-0">Head of Training & Development</p>
              <p class="card-text mt-2">Specializes in caregiver education and support.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.hover-lift {
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;
}
.hover-lift:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.list-check li {
  margin-bottom: 0.5rem;
}
</style>
